import { AppState, RedirectLoginOptions, useAuth0 } from '@auth0/auth0-react'
import { Browser } from '@capacitor/browser'

import { useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { Footer } from '../components/Footer'
import { LogoutButton } from '../components/LogoutButton'
import { ProfileIncompleteBar } from '../components/ProfileIncompleteBar'
import { Spinner } from '../components/Spinner'
import { TopLogo } from '../components/TopLogo'
import { UserAvatar } from '../components/UserAvatar'
import { AUTH0_LOGIN_SCOPE, isNativeApp } from '../config/auth0'
import { NursingSpecializations } from '../models/NursingSpecializations.model'
import { normalizePositionTitle, PositionTitles } from '../models/PositionTitles.model'
import { UserDTO } from '../models/UserDTO.model'
import { AuthStateContext } from '../providers/AuthStateContext'
import { FormService } from '../services/FormService/FormService'
import UserService from '../services/UserService/UserService'

const getPositionLabel = (value: string): string => {
  const normalized = normalizePositionTitle(value)
  const entry = Object.entries(PositionTitles).find(([, v]) => v === normalized)
  return entry ? entry[0] : value
}

const getSpecializationLabel = (value: string): string => {
  const entry = Object.entries(NursingSpecializations).find(([, v]) => v === value)
  return entry ? entry[0] : value
}

type DeleteStep = 'closed' | 'confirm' | 'extra-confirm' | 'deleted'

const Profile = (): JSX.Element => {
  const isMobileDevice = isNativeApp()
  const [deleteStep, setDeleteStep] = useState<DeleteStep>('closed')
  const { user, isAuthenticated, isLoading, loginWithRedirect } = useAuth0()
  const [userDetails, setUserDetails] = useState<UserDTO>()
  const [formUserDetails, setFormUserDetails] = useState<UserDTO>()
  const [isEditing, setIsEditing] = useState(false)
  const [editErrorMessage, setEditErrorMessage] = useState('')
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('')
  const [isSavingUser, setIsSavingUser] = useState(false)
  const [showPrimaryUnit, setShowPrimaryUnit] = useState(false)

  // Stats
  const [reviewCount, setReviewCount] = useState(0)
  const [hospitalCount, setHospitalCount] = useState(0)
  const [agencyCount, setAgencyCount] = useState(0)
  const [statsLoaded, setStatsLoaded] = useState(false)

  const history = useHistory()
  const location = useLocation()

  const nonRestrictedRoles = ['RN', 'LPN/LVN', 'RT']

  useEffect(() => {
    if (!isAuthenticated) {
      history.replace(`/login/redirect/${encodeURIComponent(location.pathname)}`)
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (isAuthenticated && user) {
      const formattedUser = UserService.formatUser(user)
      setUserDetails(formattedUser)
      setFormUserDetails(formattedUser)
      if (formattedUser.position_title) {
        setShowPrimaryUnit(nonRestrictedRoles.includes(formattedUser.position_title))
      }
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    if (!isAuthenticated) return
    const timer = setTimeout(async () => {
      try {
        const token = AuthStateContext.getToken()
        if (!token) return
        const formService = new FormService()
        const reviews = await formService.getMyReviews()
        setReviewCount(reviews.length)
        setHospitalCount(new Set(reviews.filter((r) => r.hospitalId).map((r) => r.hospitalId)).size)
        setAgencyCount(new Set(reviews.filter((r) => r.agencyId).map((r) => r.agencyId)).size)
      } catch {
        // stats are non-critical
      } finally {
        setStatsLoaded(true)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [isAuthenticated])

  useEffect(() => {
    if (deleteStep !== 'closed') {
      document.body.style.overflow = 'hidden'
      const pageRoot = document.getElementById('page-root')
      if (pageRoot) {
        pageRoot.style.overflow = 'hidden'
      }
    } else {
      document.body.style.overflow = ''
      const pageRoot = document.getElementById('page-root')
      if (pageRoot) {
        pageRoot.style.overflow = 'auto'
      }
    }
    return () => {
      document.body.style.overflow = ''
      const pageRoot = document.getElementById('page-root')
      if (pageRoot) {
        pageRoot.style.overflow = 'auto'
      }
    }
  }, [deleteStep])

  const handleEdit = async () => {
    setEditErrorMessage('')
    if (formUserDetails && userDetails) {
      setIsSavingUser(true)

      try {
        // check if the user fields was changed and if so, send a request to our API to update the user
        const changedUserFields: Partial<
          Record<'name' | 'position_title' | 'nursing_specialization' | 'work_experience' | 'work_settings', string>
        > = {}
        if (formUserDetails.name !== userDetails.name) {
          changedUserFields.name = formUserDetails.name
        }
        if (formUserDetails.position_title !== userDetails.position_title) {
          changedUserFields.position_title = formUserDetails.position_title
        }
        if (formUserDetails.nursing_specialization !== userDetails.nursing_specialization) {
          changedUserFields.nursing_specialization = formUserDetails.nursing_specialization
        }
        if (formUserDetails.work_experience !== userDetails.work_experience) {
          changedUserFields.work_experience = formUserDetails.work_experience
        }
        if (formUserDetails.work_settings !== userDetails.work_settings) {
          changedUserFields.work_settings = formUserDetails.work_settings
        }
        if (Object.keys(changedUserFields).length) {
          const newUserDetails = await UserService.updateUser(userDetails.id, changedUserFields)

          // update the user in the Auth0 session by forcing a login (which will be instant since they are already logged in)
          let loginParams: RedirectLoginOptions<AppState> = {
            appState: { redirectUrl: location.pathname },
            authorizationParams: { scope: AUTH0_LOGIN_SCOPE },
          }
          if (isMobileDevice) {
            loginParams = {
              ...loginParams,
              async openUrl(url) {
                // Redirect using Capacitor's Browser plugin
                await Browser.open({
                  url,
                  windowName: '_self',
                })
              },
            }
          }

          await loginWithRedirect(loginParams)

          setUserDetails(newUserDetails)
          setFormUserDetails(newUserDetails)
        }

        setIsEditing(false)
      } catch (e) {
        console.error(e)
        let errorMessage = 'There was an error updating your profile. Please try again or contact support.'
        const axiosError = e as { status: number; data: { message: string } }
        if ([400, 403].includes(axiosError?.status) && axiosError?.data?.message) {
          errorMessage = axiosError.data.message
        }
        setEditErrorMessage(errorMessage)
      }
      setIsSavingUser(false)
    }
  }

  const handleDelete = async () => {
    setDeleteErrorMessage('')
    setDeleteStep('closed')
    if (formUserDetails && userDetails) {
      setIsSavingUser(true)

      try {
        await UserService.deleteUser(userDetails.id)

        setDeleteStep('deleted')

        setTimeout(() => {
          history.push('/logout')
        }, 3000)
      } catch (e) {
        console.error(e)
        setDeleteErrorMessage('There was an error deleting your profile. Please try again or contact support.')
      }
      setIsSavingUser(false)
    }
  }

  return (
    <div id="page-root" className="h-screen overflow-auto relative flex flex-col">
      <TopLogo />

      <div className="relative flex-1 flex items-center justify-center">
        {(isLoading || isSavingUser) && (
          <div className="flex justify-center items-center py-20 min-h-[400px]">
            <Spinner size="lg" />
          </div>
        )}
        {!isLoading && !isSavingUser && (
          <div className="px-4 max-w-2xl w-full mx-auto py-6">
            {deleteErrorMessage && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600" tabIndex={-1}>
                {deleteErrorMessage}
              </div>
            )}

            {isAuthenticated && userDetails && (
              <>
                {/* ── Yellow incomplete-profile disclaimer ── */}
                <div className="mb-4">
                  <ProfileIncompleteBar />
                </div>

                <div className="rounded-2xl shadow-xl overflow-hidden mb-4 min-h-[420px]">
                  {/* ── Header Card ── */}
                  <div className="flex items-center gap-4 bg-aqua-mint p-7 text-white">
                    <UserAvatar size="xl" userDetails={userDetails} />
                    <div className="text-white min-w-0">
                      <h2 className="text-lg font-bold leading-tight truncate">
                        {userDetails.position_title
                          ? getPositionLabel(userDetails.position_title)
                          : 'Healthcare Professional'}
                      </h2>
                      {userDetails.nursing_specialization && (
                        <p className="text-white/80 text-sm mt-0.5 truncate">
                          {getSpecializationLabel(userDetails.nursing_specialization)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ── Stats Row ── */}
                  <div className="grid grid-cols-3 bg-white divide-x divide-gray-100">
                    {[
                      { label: 'Reviews', value: reviewCount },
                      { label: 'Hospitals', value: hospitalCount },
                      { label: 'Agencies', value: agencyCount },
                    ].map(({ label, value }) => (
                      <div key={label} className="py-4 text-center">
                        <div className="text-2xl font-bold text-graphite-grey">{value}</div>
                        <div className="text-xs text-gray-400 mt-0.5 uppercase tracking-wide">{label}</div>
                      </div>
                    ))}
                  </div>

                  {statsLoaded && reviewCount === 0 && !isEditing && (
                    <div className="border-y border-dashed border-gray-200 bg-gray-50/60 px-5 py-6 text-center">
                      <h3 className="text-base font-semibold text-graphite-grey">No reviews yet</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        You haven&apos;t reviewed a hospital or agency yet. Share your experience anonymously to help
                        other bedside staff.
                      </p>
                      <Link
                        to="/hospitals"
                        className="mt-4 inline-flex items-center justify-center rounded-lg bg-orange px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-orange/90"
                      >
                        Write Your First Review
                      </Link>
                    </div>
                  )}

                  {/* ── Professional Info / Edit Form ── */}
                  <div className="bg-white p-7">
                    {isEditing && formUserDetails ? (
                      <div className="space-y-5">
                        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-widest">Edit Profile</h3>

                        <div>
                          <label className="block text-sm font-semibold text-graphite-grey mb-2">Healthcare Role</label>
                          <select
                            name="position_title-input"
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white"
                            onChange={(evt) => {
                              const selectedPosition = evt.target.value
                              const shouldShow = nonRestrictedRoles.includes(selectedPosition)
                              setShowPrimaryUnit(shouldShow)
                              setFormUserDetails({
                                ...formUserDetails,
                                nursing_specialization: shouldShow ? formUserDetails.nursing_specialization : '',
                                position_title: selectedPosition,
                              })
                            }}
                            value={normalizePositionTitle(formUserDetails.position_title)}
                          >
                            {Object.keys(PositionTitles)
                              .sort((a, b) => a.localeCompare(b))
                              .map((label) => {
                                const value = PositionTitles[label as keyof typeof PositionTitles]
                                return (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                )
                              })}
                          </select>
                        </div>

                        {showPrimaryUnit && (
                          <div>
                            <label className="block text-sm font-semibold text-graphite-grey mb-2">
                              Primary Unit/Speciality
                            </label>
                            <select
                              name="nursing_specialization-input"
                              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white"
                              onChange={(evt) => {
                                setFormUserDetails({
                                  ...formUserDetails,
                                  nursing_specialization: evt.target.value,
                                })
                              }}
                              value={formUserDetails.nursing_specialization}
                            >
                              <option value="">-Select-</option>
                              {Object.keys(NursingSpecializations)
                                .sort((a, b) => a.localeCompare(b))
                                .map((label) => {
                                  const value = NursingSpecializations[label as keyof typeof NursingSpecializations]
                                  return (
                                    <option key={value} value={value}>
                                      {label}
                                    </option>
                                  )
                                })}
                            </select>
                          </div>
                        )}

                        {/* Years of experience */}
                        <div>
                          <label className="block text-sm font-semibold text-graphite-grey mb-2">
                            Years of experience
                          </label>
                          <select
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white"
                            value={formUserDetails.work_experience}
                            onChange={(evt) =>
                              setFormUserDetails({ ...formUserDetails, work_experience: evt.target.value })
                            }
                          >
                            <option value="">— Select —</option>
                            {['Less than 1 year', '1–2 years', '3–5 years', '6–10 years', '10+ years'].map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Work setting */}
                        <div>
                          <label className="block text-sm font-semibold text-graphite-grey mb-2">Work setting</label>
                          <select
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white"
                            value={formUserDetails.work_settings}
                            onChange={(evt) =>
                              setFormUserDetails({ ...formUserDetails, work_settings: evt.target.value })
                            }
                          >
                            <option value="">— Select —</option>
                            {['Travel', 'Staff', 'Per Diem'].map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>

                        {editErrorMessage && (
                          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600" tabIndex={-1}>
                            {editErrorMessage}
                          </div>
                        )}

                        <div className="flex gap-3 pt-1">
                          <button
                            className="flex-1 bg-orange text-white font-semibold rounded-lg py-3 hover:bg-orange/90 transition-all"
                            onClick={() => handleEdit()}
                          >
                            Save Changes
                          </button>
                          <button
                            className="flex-1 bg-gray-100 text-graphite-grey font-semibold rounded-lg py-3 hover:bg-gray-200 transition-all"
                            onClick={() => setIsEditing(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-4">
                          Professional Info
                        </h3>
                        <div className="space-y-3 mb-5">
                          <div className="flex justify-between items-center py-2 border-b border-gray-50">
                            <span className="text-sm text-gray-400">Discipline</span>
                            <span className="text-sm font-medium text-graphite-grey text-right max-w-[60%]">
                              {userDetails.position_title ? getPositionLabel(userDetails.position_title) : '—'}
                            </span>
                          </div>
                          {userDetails.position_title && nonRestrictedRoles.includes(userDetails.position_title) && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-50">
                              <span className="text-sm text-gray-400">Specialty</span>
                              <span className="text-sm font-medium text-graphite-grey text-right max-w-[60%]">
                                {userDetails.nursing_specialization
                                  ? getSpecializationLabel(userDetails.nursing_specialization)
                                  : '—'}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between items-center py-2 border-b border-gray-50">
                            <span className="text-sm text-gray-400">Experience</span>
                            <span className="text-sm font-medium text-graphite-grey text-right max-w-[60%]">
                              {userDetails.work_experience || '—'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-50">
                            <span className="text-sm text-gray-400">Work setting</span>
                            <span className="text-sm font-medium text-graphite-grey text-right max-w-[60%]">
                              {userDetails.work_settings || '—'}
                            </span>
                          </div>
                        </div>

                        <button
                          className="w-full bg-orange text-white font-semibold rounded-lg py-3 hover:bg-orange/90 transition-all"
                          onClick={() => {
                            setEditErrorMessage('')
                            setIsEditing(true)
                          }}
                        >
                          Edit Profile
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {/* end outer shadow card */}

                {/* ── Bottom Actions ── */}
                <div className="mt-3 flex items-center justify-between px-1">
                  <button
                    type="button"
                    className="rounded-lg border border-transparent px-3 py-1.5 text-sm font-semibold text-red-500 transition-all duration-150 hover:border-red-200 hover:bg-red-50 hover:text-red-600 active:border-red-200 active:bg-red-50 active:text-red-600"
                    onClick={() => setDeleteStep('confirm')}
                  >
                    Delete My Data
                  </button>
                  <LogoutButton className="rounded-lg border border-transparent px-3 py-1.5 text-sm font-semibold text-gray-600 transition-all duration-150 hover:border-gray-200 hover:bg-gray-50 hover:text-graphite-grey active:border-gray-200 active:bg-gray-50 active:text-graphite-grey" />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <Footer />

      {deleteStep === 'confirm' && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
            onClick={() => setDeleteStep('closed')}
          />
          <div className="relative z-[10001] bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">Delete Account</h2>
            <p className="text-base text-center text-gray-700 mb-6">
              Are you sure you wish to delete your account and all of your data?
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setDeleteStep('closed')}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md font-medium hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setDeleteStep('extra-confirm')}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteStep === 'extra-confirm' && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
            onClick={() => setDeleteStep('closed')}
          />
          <div className="relative z-[10001] bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">Final Confirmation</h2>
            <p className="text-base text-center text-gray-700 mb-6">Let&apos;s make sure you really want to do this.</p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setDeleteStep('closed')}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md font-medium hover:bg-gray-300 transition-colors"
              >
                Nevermind
              </button>
              <button
                onClick={() => void handleDelete()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 transition-colors"
              >
                Very sure
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteStep === 'deleted' && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm" />
          <div className="relative z-[10001] bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">Account Deleted</h2>
            <p className="text-base text-center text-gray-700">
              Your data has been deleted. You will be logged out now. We&apos;re sorry to see you go.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default Profile
